import { GetServerSidePropsContext } from 'next';
import { Cookies } from 'react-cookie';
import {
  findAdobeTargetActivity,
  getSupplementalDataId,
  IAdobeTargetActivityResponse,
  isIAdobeTargetActivityError,
  updateSectionsWithAdobeTargetActivity
} from '@belong/adobe-target';
import { COOKIES } from '@belong/constants';
import { FPage } from '@belong/contentful';
import { deleteCookie, getCookie, setCookie } from '@belong/cookies';
import { isUnderAutomatedTesting } from '@belong/env/src/device';
import type { TSSPChain } from '@belong/ssp-execution';
import { Entry, USER_TYPE } from '@belong/types';
import { IPageData } from '../core/types';
import { getParsedSection } from '../helpers/contentful';
import { TPublicSSPContext } from './types';

export interface IAdobeTargetEnrichmentProps {
  adobeTargetContext: GetServerSidePropsContext;
  correlationId: string;
  pageEntry?: Entry<any>;
  isLoggedIn: boolean;
  userType: string;
}
interface IAdobeTargetEnrichmentResponse {
  pageProps: IPageData;
  adobeTargetActivities: Record<string, IAdobeTargetActivityResponse> | undefined;
  redirectUrl?: string;
}

export const adobeTargetEnrichment = async ({
  adobeTargetContext,
  correlationId: adobeTargetCorrelationId,
  pageEntry: adobeTargetPageEntry,
  isLoggedIn: adobeTargetIsLoggedIn,
  userType: adobeTargetUserType
}: IAdobeTargetEnrichmentProps): Promise<IAdobeTargetEnrichmentResponse> => {
  // Reuse the supplemental data ID and experiment mbox carried from a prior redirect so Adobe
  // stitches both server-side hits into one visit and the experiment's analytics fire here.
  const carriedRedirect = getCookie<{ supplementalDataId: string; mBoxName: string }>(
    COOKIES.ADOBE_TARGET_REDIRECT,
    adobeTargetContext,
    { parse: true }
  );
  const supplementalDataId = carriedRedirect?.supplementalDataId ?? getSupplementalDataId();

  const forceControlExperience = false; // use this when required to force control experience for testing

  if (carriedRedirect) {
    deleteCookie(COOKIES.ADOBE_TARGET_REDIRECT, adobeTargetContext, { path: '/' });
  }

  const adobeTargetActivityResponse = await findAdobeTargetActivity({
    ctx: adobeTargetContext,
    sessionId: adobeTargetCorrelationId,
    supplementalDataId,
    unparsedPageEntry: adobeTargetPageEntry,
    additionalMboxLocation: carriedRedirect?.mBoxName,
    isAuthenticatedCustomer: adobeTargetIsLoggedIn && adobeTargetUserType === String(USER_TYPE.CUSTOMER),
    forceControlExperience
  });

  let parsedPageEntry = adobeTargetPageEntry;

  // there may not be an Adobe Target or retrieving the Activity may fail
  let adobeTargetActivities: Record<string, IAdobeTargetActivityResponse> | undefined;
  let redirectUrl: string | undefined;

  if (adobeTargetActivityResponse) {
    // inject the results of the Adobe Target Activity Response into the page entry
    const { pageEntry: updatedPageEntry, redirect } = updateSectionsWithAdobeTargetActivity({
      isAutoTest: isUnderAutomatedTesting(new Cookies(adobeTargetContext.req?.headers.cookie)),
      adobeTargetActivityResponses: adobeTargetActivityResponse,
      unparsedPageEntry: adobeTargetPageEntry,
      forceControlExperience
    });
    parsedPageEntry = updatedPageEntry;
    redirectUrl = redirect?.url;

    // Persist the supplemental data ID and the experiment mbox so the redirect destination
    // reuses the same visit and can fetch the experiment for its analytics.
    if (redirect) {
      setCookie(
        COOKIES.ADOBE_TARGET_REDIRECT,
        { supplementalDataId, mBoxName: redirect.mBoxName },
        { path: '/' },
        adobeTargetContext
      );
    }

    // create the adobeTargetActivities object to be sent to the client
    adobeTargetActivities = Object.fromEntries(
      Object.entries(adobeTargetActivityResponse).map(([mboxLocation, activityResponse]) => [
        mboxLocation,
        isIAdobeTargetActivityError(activityResponse) ? activityResponse : { ...activityResponse, supplementalDataId }
      ])
    );
  }

  // Merge FPage result into pageProps
  const pageProps = await FPage([parsedPageEntry], getParsedSection);

  return { pageProps, adobeTargetActivities, redirectUrl };
};

export const adobeTargetSSP: TSSPChain<TPublicSSPContext> = async ctx => {
  const { correlationId } = ctx.appServerSideProps;
  const { pageEntry, isLoggedIn, userType } = ctx;

  const { pageProps, adobeTargetActivities, redirectUrl } = await adobeTargetEnrichment({
    adobeTargetContext: ctx.context,
    correlationId,
    pageEntry,
    isLoggedIn: isLoggedIn ?? false,
    userType: userType ?? ''
  });

  return {
    ...ctx,
    adobeTargetActivities,
    redirectUrl,
    pageProps: { ...ctx.pageProps, ...pageProps }
  };
};
